import React, { useState } from 'react';
import { NavLink, Link, Outlet, useNavigate } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, AppBar, IconButton, Avatar, Menu, MenuItem,
  Divider, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKey';
import BarChartIcon from '@mui/icons-material/BarChart';
import DomainIcon from '@mui/icons-material/Language';
import WebhookIcon from '@mui/icons-material/Webhook';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AppsIcon from '@mui/icons-material/Apps';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HubIcon from '@mui/icons-material/Hub';
import { useAuth } from '../auth/AuthContext';
import wordmark from '../assets/vectros-wordmark-light.svg';

const DRAWER_WIDTH = 220;

const NAV = [
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
  { label: 'API Keys', to: '/keys', icon: <KeyIcon fontSize="small" /> },
  { label: 'Usage', to: '/usage', icon: <BarChartIcon fontSize="small" /> },
  { label: 'Domains', to: '/domains', icon: <DomainIcon fontSize="small" /> },
  { label: 'Webhooks', to: '/webhooks', icon: <WebhookIcon fontSize="small" /> },
  { label: 'Model Providers', to: '/providers', icon: <HubIcon fontSize="small" /> },
  { label: 'API Logs', to: '/logs', icon: <ReceiptLongIcon fontSize="small" /> },
  { label: 'Trigger Failures', to: '/trigger-failures', icon: <ErrorOutlineIcon fontSize="small" /> },
  { label: 'Billing', to: '/billing', icon: <CreditCardIcon fontSize="small" /> },
  { label: 'Invite Peers', to: '/invite', icon: <GroupAddIcon fontSize="small" /> },
  { label: 'Settings', to: '/settings', icon: <SettingsIcon fontSize="small" /> },
  { label: 'API Reference', to: '/docs', icon: <MenuBookIcon fontSize="small" /> },
  { label: 'Apps', to: '/apps', icon: <AppsIcon fontSize="small" /> },
];

function DrawerContents({ onNavigate }) {
  return (
    <>
      <Box
        component={Link}
        to="/dashboard"
        sx={{ px: 2.5, py: 2, display: 'block', textDecoration: 'none' }}
      >
        <img src={wordmark} alt="Vectros" style={{ height: 28, display: 'block' }} />
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ px: 1, pt: 1 }}>
        {NAV.map(({ label, to, icon }) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            onClick={onNavigate}
            sx={{
              borderRadius: '6px',
              mb: 0.5,
              color: '#A1A1AA',
              '&.active': { bgcolor: 'rgba(59,130,246,0.15)', color: 'white' },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: 'white' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{icon}</ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
          </ListItemButton>
        ))}
      </List>
    </>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawerSx = {
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      bgcolor: '#09090B',
      color: 'white',
      borderRight: 'none',
    },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Permanent sidebar — desktop only */}
      <Drawer
        variant="permanent"
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, display: { xs: 'none', md: 'block' }, ...drawerSx }}
      >
        <DrawerContents />
      </Drawer>

      {/* Temporary drawer — mobile only */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, ...drawerSx }}
      >
        <DrawerContents onNavigate={() => setMobileOpen(false)} />
      </Drawer>

      {/* Main area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="static" elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ minHeight: '56px !important' }}>
            {isMobile && (
              <IconButton edge="start" aria-label="Open navigation menu" onClick={() => setMobileOpen(true)} sx={{ mr: 1, color: 'text.primary' }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Account">
              <IconButton aria-label="Account menu" onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#3B82F6', fontSize: 13 }}>
                  {user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'D'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled sx={{ fontSize: 13 }}>{user?.email}</MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ fontSize: 13 }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
